import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import {
  Truck,
  MapPin,
  Clock,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  ArrowLeft,
  Navigation,
  Filter,
  Calendar,
  Activity,
  Gauge,
  Wifi,
  WifiOff,
} from "lucide-react";

const SeguimientoFlota = ({ onVolverAtras }) => {
  const { user } = useAuth();

  const getFechaHoy = () => {
    const fecha = new Date();
    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, "0");
    const day = String(fecha.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const API_BASE_URL = process.env.REACT_APP_VILOX_FLOTA_API || "http://localhost:8080";
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;

  const getApiHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      "ngrok-skip-browser-warning": "true",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  // Helper para construir la URL con token si es necesario en producción
  const buildUrl = (path, params = {}) => {
    const token = localStorage.getItem("token");
    const url = new URL(`${API_BASE_URL}${path}`);
    
    // Agregar parámetros existentes
    Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));
    
    // Agregar token en producción
    if (token && window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1") {
      url.searchParams.append("token", token);
    }
    return url.toString();
  };

  const [vehiculos, setVehiculos] = useState([]);
  const [selectedVehiculo, setSelectedVehiculo] = useState(null);
  const [trackingData, setTrackingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTracking, setLoadingTracking] = useState(false);

  const [tiempoReal, setTiempoReal] = useState(true);
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null);

  const [fechaInicio, setFechaInicio] = useState(getFechaHoy());
  const [fechaFin, setFechaFin] = useState(getFechaHoy());

  const mapContainerRef = useRef(null);
  const isInitialized = useRef(false);
  const intervalRef = useRef(null);
  const [toasts, setToasts] = useState([]);

  const formatearFechaLocal = (fecha) => {
    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, "0");
    const day = String(fecha.getDate()).padStart(2, "0");
    const hours = String(fecha.getHours()).padStart(2, "0");
    const minutes = String(fecha.getMinutes()).padStart(2, "0");
    const seconds = String(fecha.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const showToast = (message, type = "success") => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type };
    setToasts((prev) => [...prev, newToast]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 3000);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const loadGoogleMapsAPI = () => {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.maps) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=drawing,places,geometry&v=3.64`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        if (window.google && window.google.maps) {
          resolve();
        } else {
          reject(new Error("Google Maps API failed to load"));
        }
      };

      script.onerror = () =>
        reject(new Error("Google Maps API script failed to load"));
      document.head.appendChild(script);
    });
  };

  const initializeMap = () => {
    if (!mapContainerRef.current || !window.google) {
      console.error("Map container or Google Maps API not available");
      return;
    }

    try {
      const peruCenter = { lat: -12.0464, lng: -77.0428 };

      window.flotaMap = new window.google.maps.Map(mapContainerRef.current, {
        zoom: 8,
        center: peruCenter,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "water",
            elementType: "geometry",
            stylers: [{ color: "#e3f2fd" }],
          },
          {
            featureType: "landscape",
            elementType: "geometry",
            stylers: [{ color: "#f5f5f5" }],
          },
        ],
      });

      window.flotaPolyline = null;
      window.flotaMarkers = [];

      console.log("🎉 Google Maps inicializado para seguimiento de flota");
      showToast("Mapa de seguimiento inicializado");
    } catch (error) {
      console.error("Error initializing Google Maps:", error);
      showToast("Error al inicializar Google Maps", "error");
    }
  };

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const initializeEverything = async () => {
      try {
        await loadGoogleMapsAPI();
        initializeMap();
        await fetchVehiculos();
      } catch (error) {
        console.error("Error initializing:", error);
        showToast("Error al inicializar el mapa", "error");
      }
    };

    initializeEverything();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const fetchVehiculos = async () => {
    if (!user) {
      console.error("Usuario no autenticado");
      return;
    }

    try {
      setLoading(true);

      const url = buildUrl(`/api/transportista/${user.empresa_id || 'null'}`, { tipoEmpresa: 'transportista' });

      console.log("📡 Cargando vehículos del transportista...");

      const response = await fetch(url, {
        headers: getApiHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Datos recibidos:", data);

        const vehiculosData = data.camiones || [];
        setVehiculos(vehiculosData);

        if (vehiculosData.length > 0) {
          setSelectedVehiculo(vehiculosData[0]);
        }

        showToast(`${vehiculosData.length} vehículos cargados`);
      } else {
        console.error("Error cargando vehículos:", response.status);
        showToast("Error al cargar vehículos", "error");
      }
    } catch (error) {
      console.error("Error de conexión:", error);
      showToast("Error de conexión al cargar vehículos", "error");
    } finally {
      setLoading(false);
    }
  };

  const clearMap = () => {
    if (!window.google || !window.flotaMap) return;

    if (window.flotaPolyline) {
      window.flotaPolyline.setMap(null);
      window.flotaPolyline = null;
    }

    if (window.flotaMarkers) {
      window.flotaMarkers.forEach((marker) => marker.setMap(null));
      window.flotaMarkers = [];
    }

    console.log("🧹 Mapa limpiado");
  };

  const updateMapWithTracking = (tracking, vehiculo, isRealTime = false) => {
    if (!window.google || !window.flotaMap || !tracking.length) {
      console.log("Google Maps no disponible o sin datos GPS");
      return;
    }

    clearMap();

    try {
      const coordinates = tracking
        .map((point) => ({
          lat: parseFloat(point.latitude),
          lng: parseFloat(point.longitude),
          timestamp: point.timestamp || point.creationDate || point.gpstime,
          speed: point.speed || 0,
          altitude: point.altitude,
          accuracy: point.accuracy,
        }))
        .filter((coord) => !isNaN(coord.lat) && !isNaN(coord.lng));

      if (coordinates.length === 0) {
        showToast("Sin coordenadas GPS válidas", "error");
        return;
      }

      window.flotaPolyline = new window.google.maps.Polyline({
        path: coordinates,
        geodesic: true,
        strokeColor: isRealTime ? "#10b981" : "#2563eb",
        strokeOpacity: 0.8,
        strokeWeight: 4,
      });

      window.flotaPolyline.setMap(window.flotaMap);

      const currentMarker = new window.google.maps.Marker({
        position: coordinates[coordinates.length - 1],
        map: window.flotaMap,
        title: `${vehiculo.placa} - Última posición`,
        icon: {
          path: "M20,8h-3V4H3C1.89,4 1,4.89 1,6v11h2c0,1.66 1.34,3 3,3s3,-1.34 3,-3h6c0,1.66 1.34,3 3,3s3,-1.34 3,-3h2V13L20,8zM6,18.5c-0.83,0 -1.5,-0.67 -1.5,-1.5s0.67,-1.5 1.5,-1.5s1.5,0.67 1.5,1.5S6.83,18.5 6,18.5zM18,18.5c-0.83,0 -1.5,-0.67 -1.5,-1.5s0.67,-1.5 1.5,-1.5s1.5,0.67 1.5,1.5S18.83,18.5 18,18.5zM17,5v3h2.5l1.96,2.5H17V9H9V7h8V5z",
          fillColor: isRealTime ? "#10b981" : "#2563eb",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
          scale: 1.5,
          anchor: new window.google.maps.Point(12, 12),
        },
        animation: isRealTime ? window.google.maps.Animation.BOUNCE : null,
      });

      window.flotaMarkers = [currentMarker];

      if (!isRealTime && coordinates.length > 1) {
        const startMarker = new window.google.maps.Marker({
          position: coordinates[0],
          map: window.flotaMap,
          title: "Inicio de ruta",
          icon: {
            path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
            fillColor: "#16a34a",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 2,
            scale: 1.2,
            anchor: new window.google.maps.Point(12, 24),
          },
        });
        window.flotaMarkers.push(startMarker);
      }

      if (isRealTime) {
        const currentPosition = coordinates[coordinates.length - 1];
        window.flotaMap.setCenter(currentPosition);
        window.flotaMap.setZoom(10);
      } else {
        const bounds = new window.google.maps.LatLngBounds();
        coordinates.forEach((coord) => bounds.extend(coord));
        window.flotaMap.fitBounds(bounds, { padding: 50 });
      }

      const mensaje = isRealTime
        ? `Posición en tiempo real de ${vehiculo.placa}`
        : `Ruta histórica de ${vehiculo.placa}`;
      showToast(mensaje);
    } catch (error) {
      console.error("Error updating map:", error);
      showToast("Error al actualizar el mapa", "error");
    }
  };

  const loadRealTimeTracking = async (vehiculoId, vehiculo) => {
    if (!vehiculoId) return;

    try {
      setLoadingTracking(true);

      const ahora = new Date();
      const hace24Horas = new Date(ahora.getTime() - 24 * 60 * 60 * 1000);

      const inicio = formatearFechaLocal(hace24Horas);
      const fin = formatearFechaLocal(ahora);

      // ✅ LOG 1: Datos que se envían al backend
      console.log("🚀 ===== PETICIÓN TIEMPO REAL =====");
      console.log("📋 Datos del vehículo:", {
        id: vehiculoId,
        placa: vehiculo.placa,
        marca: vehiculo.marca,
        modelo: vehiculo.modelo,
      });
      console.log("📅 Rango de fechas:", {
        inicio: inicio,
        fin: fin,
      });

      const url = buildUrl('/api/tracks-by-vehicle', { vehiculoId, inicio, fin });
      console.log("🔗 URL completa:", url);

      const response = await fetch(url, {
        headers: getApiHeaders(),
      });

      if (response.ok) {
        const data = await response.json();

        // ✅ LOG 2: Datos que devuelve el backend
        console.log("✅ ===== RESPUESTA DEL BACKEND =====");
        console.log("📊 Total de puntos GPS recibidos:", data.length);
        console.log("📍 Primer punto:", data[0]);
        console.log("📍 Último punto:", data[data.length - 1]);

        const recentData = data.slice(-100);

        // ✅ LOG 3: Análisis de datos procesados
        if (recentData.length > 0) {
          const distancia = calcularDistanciaTotal(recentData);
          const velocidadProm = calcularVelocidadPromedio(recentData);

          console.log("📈 ===== ESTADÍSTICAS CALCULADAS =====");
          console.log("🚗 Vehículo:", vehiculo.placa);
          console.log("📏 Distancia total recorrida:", distancia, "km");
          console.log("⚡ Velocidad promedio:", velocidadProm, "km/h");
          console.log("📌 Puntos procesados:", recentData.length);
          console.log("🌍 Coordenadas actuales:", {
            lat: recentData[recentData.length - 1].latitude,
            lng: recentData[recentData.length - 1].longitude,
          });
          // ✅ AQUÍ VA EL CÓDIGO DE GEOCODIFICACIÓN
          const ubicacionInicio = await obtenerNombreCiudad(
            recentData[0].latitude,
            recentData[0].longitude
          );
          const ubicacionFin = await obtenerNombreCiudad(
            recentData[recentData.length - 1].latitude,
            recentData[recentData.length - 1].longitude
          );
          console.log("🗺️ Ubicación inicio:", ubicacionInicio);
          console.log("🗺️ Ubicación actual/fin:", ubicacionFin);
        }

        setTrackingData(recentData);
        setUltimaActualizacion(new Date());

        if (recentData.length > 0) {
          updateMapWithTracking(recentData, vehiculo, true);
        } else {
          clearMap();
          showToast("Sin posición GPS reciente", "error");
        }
      } else {
        console.error("❌ Error cargando GPS:", response.status);
        setTrackingData([]);
        clearMap();
      }
    } catch (error) {
      console.error("❌ Error de conexión:", error);
      setTrackingData([]);
      clearMap();
    } finally {
      setLoadingTracking(false);
    }
  };

  const loadHistoricalTracking = async (vehiculoId, vehiculo) => {
    if (!vehiculoId || !fechaInicio || !fechaFin) {
      showToast("Selecciona el rango de fechas", "error");
      return;
    }

    try {
      setLoadingTracking(true);

      const inicio = `${fechaInicio}T00:00:00`;
      const fin = `${fechaFin}T23:59:59`;

      // ✅ LOG 1: Datos que se envían al backend
      console.log("🚀 ===== PETICIÓN HISTÓRICA =====");
      console.log("📋 Datos del vehículo:", {
        id: vehiculoId,
        placa: vehiculo.placa,
        marca: vehiculo.marca,
        modelo: vehiculo.modelo,
      });
      console.log("📅 Período seleccionado:", {
        inicio: inicio,
        fin: fin,
        dias: Math.ceil(
          (new Date(fin) - new Date(inicio)) / (1000 * 60 * 60 * 24)
        ),
      });

      const url = `${API_BASE_URL}/api/tracks-by-vehicle?vehiculoId=${vehiculoId}&inicio=${inicio}&fin=${fin}`;
      console.log("🔗 URL completa:", url);

      const response = await fetch(url, {
        headers: getApiHeaders(),
      });

      if (response.ok) {
        const data = await response.json();

        // ✅ LOG 2: Datos que devuelve el backend
        console.log("✅ ===== RESPUESTA DEL BACKEND =====");
        console.log("📊 Total de puntos GPS recibidos:", data.length);
        if (data.length > 0) {
          console.log("📍 Primer punto:", data[0]);
          console.log("📍 Último punto:", data[data.length - 1]);

          // ✅ LOG 3: Análisis detallado
          const distancia = calcularDistanciaTotal(data);
          const velocidadProm = calcularVelocidadPromedio(data);
          const velocidadMax = Math.max(
            ...data.map((p) => parseFloat(p.speed) || 0)
          );

          console.log("📈 ===== ESTADÍSTICAS CALCULADAS =====");
          console.log("🚗 Vehículo:", vehiculo.placa);
          console.log("📏 Distancia total recorrida:", distancia, "km");
          console.log("⚡ Velocidad promedio:", velocidadProm, "km/h");
          console.log("🏁 Velocidad máxima:", velocidadMax.toFixed(1), "km/h");
          console.log("📌 Total de puntos GPS:", data.length);
          console.log(
            "⏱️ Duración del viaje:",
            (() => {
              const inicio = new Date(data[0].creationDate);
              const fin = new Date(data[data.length - 1].creationDate);
              const duracion = fin - inicio;
              const horas = Math.floor(duracion / (1000 * 60 * 60));
              const minutos = Math.floor(
                (duracion % (1000 * 60 * 60)) / (1000 * 60)
              );
              return `${horas}h ${minutos}m`;
            })()
          );

          // ✅ AQUÍ VA EL CÓDIGO DE GEOCODIFICACIÓN
          const ubicacionInicio = await obtenerNombreCiudad(
            data[0].latitude,
            data[0].longitude
          );
          const ubicacionFin = await obtenerNombreCiudad(
            data[data.length - 1].latitude,
            data[data.length - 1].longitude
          );
          console.log("🗺️ Ubicación inicio:", ubicacionInicio);
          console.log("🗺️ Ubicación final:", ubicacionFin);
        }

        setTrackingData(data);

        if (data.length > 0) {
          updateMapWithTracking(data, vehiculo, false);
        } else {
          clearMap();
          showToast("No hay datos GPS para el período seleccionado", "error");
        }
      } else {
        console.error("❌ Error cargando datos:", response.status);
        setTrackingData([]);
        clearMap();
      }
    } catch (error) {
      console.error("❌ Error de conexión:", error);
      setTrackingData([]);
      clearMap();
    } finally {
      setLoadingTracking(false);
    }
  };

  useEffect(() => {
    // Limpiar intervalo anterior
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (tiempoReal && selectedVehiculo?.id) {
      loadRealTimeTracking(selectedVehiculo.id, selectedVehiculo);

      intervalRef.current = setInterval(() => {
        loadRealTimeTracking(selectedVehiculo.id, selectedVehiculo);
      }, 30000);

      console.log("⏱️ Actualización automática activada (cada 30s)");
    } else {
      console.log("⏸️ Actualización automática desactivada");
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [tiempoReal, selectedVehiculo]);

  const formatTime = (dateString) => {
    if (!dateString) return "--:--";
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Sin fecha";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Fecha inválida";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const calcularDistanciaTotal = (tracking) => {
    if (tracking.length < 2) {
      console.log(
        "⚠️ Insuficientes puntos para calcular distancia:",
        tracking.length
      );
      return 0;
    }

    console.log("📐 ===== CALCULANDO DISTANCIA =====");
    console.log("🔢 Puntos a procesar:", tracking.length);

    // Fórmula de Haversine para calcular distancia entre 2 coordenadas GPS
    const calcularDistancia = (lat1, lng1, lat2, lng2) => {
      const R = 6371; // Radio de la Tierra en kilómetros
      const dLat = ((lat2 - lat1) * Math.PI) / 180;
      const dLng = ((lng2 - lng1) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    };

    let distanciaTotal = 0;
    let segmentos = [];

    for (let i = 1; i < tracking.length; i++) {
      const punto1 = tracking[i - 1];
      const punto2 = tracking[i];

      const lat1 = parseFloat(punto1.latitude);
      const lng1 = parseFloat(punto1.longitude);
      const lat2 = parseFloat(punto2.latitude);
      const lng2 = parseFloat(punto2.longitude);

      if (!isNaN(lat1) && !isNaN(lng1) && !isNaN(lat2) && !isNaN(lng2)) {
        const distanciaSegmento = calcularDistancia(lat1, lng1, lat2, lng2);
        distanciaTotal += distanciaSegmento;

        // Guardar primeros 3 segmentos para logging
        if (i <= 3) {
          segmentos.push({
            segmento: i,
            desde: { lat: lat1.toFixed(6), lng: lng1.toFixed(6) },
            hasta: { lat: lat2.toFixed(6), lng: lng2.toFixed(6) },
            distancia: distanciaSegmento.toFixed(3) + " km",
          });
        }
      }
    }

    console.log("📊 Primeros 3 segmentos calculados:", segmentos);
    console.log("✅ Distancia total:", distanciaTotal.toFixed(2), "km");
    console.log(
      "📏 Fórmula usada: Haversine (distancia más corta sobre esfera)"
    );

    return distanciaTotal.toFixed(1);
  };

  const calcularVelocidadPromedio = (tracking) => {
    const puntosConVelocidad = tracking.filter(
      (point) =>
        point.speed !== null &&
        point.speed !== undefined &&
        !isNaN(parseFloat(point.speed))
    );
    if (puntosConVelocidad.length === 0) return 0;

    const velocidadPromedio =
      puntosConVelocidad.reduce(
        (sum, point) => sum + parseFloat(point.speed),
        0
      ) / puntosConVelocidad.length;

    return velocidadPromedio.toFixed(1);
  };

  const obtenerNombreCiudad = async (lat, lng) => {
    try {
      const geocoder = new window.google.maps.Geocoder();
      const latlng = { lat: parseFloat(lat), lng: parseFloat(lng) };

      const response = await geocoder.geocode({ location: latlng });

      if (response.results[0]) {
        const addressComponents = response.results[0].address_components;
        const ciudad = addressComponents.find((c) =>
          c.types.includes("locality")
        )?.long_name;
        const region = addressComponents.find((c) =>
          c.types.includes("administrative_area_level_1")
        )?.long_name;

        console.log("🌍 Ubicación:", {
          coordenadas: `${lat}, ${lng}`,
          ciudad: ciudad || "No identificada",
          region: region || "No identificada",
          direccion_completa: response.results[0].formatted_address,
        });

        return {
          ciudad: ciudad || "Desconocida",
          region: region || "Desconocida",
          direccion: response.results[0].formatted_address,
        };
      }
    } catch (error) {
      console.error("Error geocodificando:", error);
      return null;
    }
  };
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg backdrop-blur-sm max-w-sm animate-in slide-in-from-right-full duration-300 ${toast.type === "error"
              ? "bg-red-500/90 text-white border border-red-400"
              : "bg-emerald-500/90 text-white border border-emerald-400"
              }`}
          >
            {toast.type === "error" ? (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="text-sm font-medium">{toast.message}</span>
            <button
              onClick={() => removeToast(toast.id)}
              className="ml-auto p-1 hover:bg-white/20 rounded-lg transition-colors"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={onVolverAtras}
                className="p-2 hover:bg-gray-100 rounded-lg mr-3 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div className="bg-blue-500 p-2 rounded-lg mr-3">
                <Truck className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Seguimiento de Flota
                </h1>
                <p className="text-sm text-gray-600">
                  Monitoreo GPS de vehículos en tiempo real
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 text-sm">
                <div
                  className={`w-3 h-3 rounded-full ${tiempoReal && trackingData.length > 0
                    ? "bg-green-500 animate-pulse"
                    : "bg-gray-400"
                    }`}
                ></div>
                <span className="text-gray-600">
                  {tiempoReal ? "Tiempo Real" : "Modo Histórico"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-3 py-2 space-y-2">
        {/* Panel de Control */}
        <div className="bg-white rounded-lg shadow-sm p-3">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
            {/* Selector de Vehículo */}
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Vehículo
              </label>
              <select
                value={selectedVehiculo?.id || ""}
                onChange={(e) => {
                  const vehiculo = vehiculos.find(
                    (v) => v.id === e.target.value
                  );
                  setSelectedVehiculo(vehiculo);
                  clearMap();
                  setTrackingData([]);
                }}
                className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              >
                <option value="">Seleccionar vehículo...</option>
                {vehiculos.map((vehiculo) => (
                  <option key={vehiculo.id} value={vehiculo.id}>
                    {vehiculo.placa} - {vehiculo.marca} {vehiculo.modelo}
                  </option>
                ))}
              </select>
            </div>

            {/* Toggle Tiempo Real */}
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Modo
              </label>
              <button
                onClick={() => {
                  const nuevoModo = !tiempoReal;
                  setTiempoReal(nuevoModo);
                  clearMap();
                  setTrackingData([]);

                  if (!nuevoModo && selectedVehiculo?.id) {
                    setTimeout(() => {
                      loadHistoricalTracking(
                        selectedVehiculo.id,
                        selectedVehiculo
                      );
                    }, 100);
                  }
                }}
                className={`w-full p-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${tiempoReal
                  ? "bg-green-500 text-white hover:bg-green-600"
                  : "bg-gray-300 text-gray-700 hover:bg-gray-400"
                  }`}
              >
                {tiempoReal ? (
                  <Wifi className="h-4 w-4" />
                ) : (
                  <WifiOff className="h-4 w-4" />
                )}
                {tiempoReal ? "Tiempo Real" : "Histórico"}
              </button>
            </div>

            {!tiempoReal && (
              <>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Fecha Inicio
                  </label>
                  <input
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Fecha Fin
                  </label>
                  <input
                    type="date"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <button
                    onClick={() => {
                      if (selectedVehiculo?.id) {
                        loadHistoricalTracking(
                          selectedVehiculo.id,
                          selectedVehiculo
                        );
                      } else {
                        showToast("Selecciona un vehículo", "error");
                      }
                    }}
                    disabled={loadingTracking || !selectedVehiculo}
                    className="w-full p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <Filter className="h-4 w-4" />
                    Filtrar
                  </button>
                </div>
              </>
            )}

            {/* Botón Refrescar (Solo en modo Tiempo Real) */}
            {tiempoReal && (
              <div className="md:col-span-2 md:col-start-6">
                <button
                  onClick={() => {
                    if (selectedVehiculo?.id) {
                      loadRealTimeTracking(
                        selectedVehiculo.id,
                        selectedVehiculo
                      );
                    }
                  }}
                  disabled={loadingTracking || !selectedVehiculo}
                  className="w-full p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${loadingTracking ? "animate-spin" : ""
                      }`}
                  />
                  Actualizar
                </button>
              </div>
            )}
          </div>

          {/* Información de última actualización */}
          {tiempoReal && ultimaActualizacion && (
            <div className="mt-2 text-xs text-gray-600 flex items-center">
              <Clock className="h-3 w-3 mr-1" />
              Última actualización: {ultimaActualizacion.toLocaleTimeString()}
              <span className="ml-2 text-green-600">
                • Actualización automática cada 30s
              </span>
            </div>
          )}
        </div>

        {/* Mapa y Detalles */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
          {/* Mapa */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-4 py-3 text-white">
              <h3 className="text-lg font-bold flex items-center">
                <MapPin className="mr-2 h-5 w-5" />
                {tiempoReal ? "Seguimiento en Tiempo Real" : "Ruta Histórica"}
              </h3>
              <p className="text-blue-100 mt-1 text-sm">
                {selectedVehiculo
                  ? `${selectedVehiculo.placa} - ${trackingData.length} puntos GPS`
                  : "Selecciona un vehículo para comenzar"}
              </p>
            </div>

            <div className="relative">
              <div
                ref={mapContainerRef}
                style={{ height: "450px", width: "100%" }}
                className="google-map-container"
              />

              {!selectedVehiculo && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/90 backdrop-blur-sm z-10">
                  <div className="text-center">
                    <Truck className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Selecciona un Vehículo
                    </h3>
                    <p className="text-gray-600 max-w-md">
                      Elige un vehículo de tu flota para visualizar su posición
                      GPS
                    </p>
                  </div>
                </div>
              )}

              {selectedVehiculo &&
                trackingData.length === 0 &&
                !loadingTracking && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/90 backdrop-blur-sm z-10">
                    <div className="text-center">
                      <AlertCircle className="h-16 w-16 text-orange-500 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Sin Datos GPS
                      </h3>
                      <p className="text-gray-600 max-w-md">
                        No hay registros GPS disponibles para{" "}
                        {selectedVehiculo.placa}
                        {!tiempoReal &&
                          ` en el período ${formatDate(
                            fechaInicio
                          )} - ${formatDate(fechaFin)}`}
                      </p>
                    </div>
                  </div>
                )}

              {loadingTracking && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/90 backdrop-blur-sm z-10">
                  <div className="text-center">
                    <RefreshCw className="h-12 w-12 text-blue-500 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Cargando datos GPS...</p>
                  </div>
                </div>
              )}
            </div>

            {/* Leyenda del Mapa */}
            {selectedVehiculo && trackingData.length > 0 && (
              <div className="bg-gray-50 border-t p-3">
                <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                  <Activity className="h-4 w-4 mr-1" />
                  Información de la Ruta
                </h4>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="flex items-center">
                    <div
                      className={`w-4 h-4 rounded mr-2 ${tiempoReal ? "bg-green-500" : "bg-blue-500"
                        }`}
                    ></div>
                    <div>
                      <span className="text-gray-600 text-xs block">Tipo</span>
                      <span className="font-medium">
                        {tiempoReal ? "En vivo" : "Histórica"}
                      </span>
                    </div>
                  </div>

                  <div>
                    <span className="text-gray-600 text-xs block">
                      Puntos GPS
                    </span>
                    <span className="font-medium">{trackingData.length}</span>
                  </div>

                  <div>
                    <span className="text-gray-600 text-xs block">
                      Distancia
                    </span>
                    <span className="font-medium">
                      {calcularDistanciaTotal(trackingData)} km
                    </span>
                  </div>

                  <div>
                    <span className="text-gray-600 text-xs block">
                      Vel. Promedio
                    </span>
                    <span className="font-medium">
                      {calcularVelocidadPromedio(trackingData)} km/h
                    </span>
                  </div>
                </div>

                {!tiempoReal && trackingData.length > 1 && (
                  <div className="mt-2 pt-2 border-t">
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <span className="text-gray-600 block">Inicio:</span>
                        <span className="font-medium">
                          {(() => {
                            const primerPunto = trackingData[0];
                            if (primerPunto?.creationDate) {
                              return new Date(
                                primerPunto.creationDate
                              ).toLocaleString();
                            }
                            return "N/A";
                          })()}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 block">Fin:</span>
                        <span className="font-medium">
                          {(() => {
                            const ultimoPunto =
                              trackingData[trackingData.length - 1];
                            if (ultimoPunto?.creationDate) {
                              return new Date(
                                ultimoPunto.creationDate
                              ).toLocaleString();
                            }
                            return "N/A";
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Panel de Detalles del Vehículo */}
          <div className="space-y-2">
            {selectedVehiculo ? (
              <>
                {/* Información del Vehículo */}
                <div className="bg-white rounded-lg shadow-sm p-3">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center text-sm">
                    <Truck className="h-4 w-4 mr-2" />
                    Información del Vehículo
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between pb-2 border-b">
                      <span className="text-gray-600 text-xs">Placa:</span>
                      <span className="font-bold text-lg text-blue-600">
                        {selectedVehiculo.placa}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-xs">Marca:</span>
                      <span className="font-medium text-sm">
                        {selectedVehiculo.marca}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-xs">Modelo:</span>
                      <span className="font-medium text-sm">
                        {selectedVehiculo.modelo}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-xs">Capacidad:</span>
                      <span className="font-medium text-sm">
                        {selectedVehiculo.capacidadToneladas} ton
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-xs">Estado:</span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${selectedVehiculo.activo
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                          }`}
                      >
                        {selectedVehiculo.activo ? "Activo" : "Inactivo"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Estado GPS */}
                {trackingData.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm p-3">
                    <h3 className="font-semibold text-gray-900 mb-2 flex items-center text-sm">
                      <Navigation className="h-4 w-4 mr-2" />
                      {tiempoReal ? "Estado Actual" : "Última Posición"}
                    </h3>

                    {(() => {
                      const ultimoPunto = trackingData[trackingData.length - 1];
                      return (
                        <div className="space-y-2 text-sm">
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-gray-600 text-xs">
                                Velocidad:
                              </span>
                              <div className="flex items-center">
                                <Gauge className="h-4 w-4 text-blue-500 mr-1" />
                                <span className="font-bold text-lg text-blue-600">
                                  {ultimoPunto.speed
                                    ? parseFloat(ultimoPunto.speed).toFixed(0)
                                    : "0"}{" "}
                                  km/h
                                </span>
                              </div>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-500 h-2 rounded-full transition-all"
                                style={{
                                  width: `${Math.min(
                                    ((parseFloat(ultimoPunto.speed) || 0) /
                                      100) *
                                    100,
                                    100
                                  )}%`,
                                }}
                              />
                            </div>
                          </div>

                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-600 text-xs">
                              Coordenadas:
                            </span>
                            <span className="font-medium text-xs">
                              {parseFloat(ultimoPunto.latitude).toFixed(6)},{" "}
                              {parseFloat(ultimoPunto.longitude).toFixed(6)}
                            </span>
                          </div>

                          {ultimoPunto.altitude && (
                            <div className="flex justify-between py-2 border-b">
                              <span className="text-gray-600 text-xs">
                                Altitud:
                              </span>
                              <span className="font-medium text-xs">
                                {ultimoPunto.altitude} m
                              </span>
                            </div>
                          )}

                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-600 text-xs">
                              Última señal:
                            </span>
                            <span className="font-medium text-xs">
                              {ultimoPunto.creationDate
                                ? new Date(
                                  ultimoPunto.creationDate
                                ).toLocaleString()
                                : "N/A"}
                            </span>
                          </div>

                          {tiempoReal && (
                            <div className="mt-3 p-2 bg-green-50 rounded-lg flex items-center">
                              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></div>
                              <span className="text-xs text-green-700 font-medium">
                                Seguimiento activo
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Estadísticas del Período */}
                {!tiempoReal && trackingData.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm p-3">
                    <h3 className="font-semibold text-gray-900 mb-2 flex items-center text-sm">
                      <Calendar className="h-4 w-4 mr-2" />
                      Estadísticas del Período
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 text-xs">Duración:</span>
                        <span className="font-medium text-xs">
                          {(() => {
                            const primerPunto = trackingData[0];
                            const ultimoPunto =
                              trackingData[trackingData.length - 1];
                            if (
                              primerPunto?.creationDate &&
                              ultimoPunto?.creationDate
                            ) {
                              const inicio = new Date(primerPunto.creationDate);
                              const fin = new Date(ultimoPunto.creationDate);
                              const duracion = fin - inicio;
                              const horas = Math.floor(
                                duracion / (1000 * 60 * 60)
                              );
                              const minutos = Math.floor(
                                (duracion % (1000 * 60 * 60)) / (1000 * 60)
                              );
                              return `${horas}h ${minutos}m`;
                            }
                            return "N/A";
                          })()}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-600 text-xs">
                          Distancia total:
                        </span>
                        <span className="font-medium text-xs">
                          {calcularDistanciaTotal(trackingData)} km
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-600 text-xs">
                          Vel. máxima:
                        </span>
                        <span className="font-medium text-xs">
                          {Math.max(
                            ...trackingData.map((p) => parseFloat(p.speed) || 0)
                          ).toFixed(0)}{" "}
                          km/h
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-600 text-xs">
                          Vel. promedio:
                        </span>
                        <span className="font-medium text-xs">
                          {calcularVelocidadPromedio(trackingData)} km/h
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-600 text-xs">
                          Puntos registrados:
                        </span>
                        <span className="font-medium text-xs">
                          {trackingData.length}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                <Truck className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Sin vehículo seleccionado
                </h3>
                <p className="text-gray-600 text-sm">
                  Selecciona un vehículo para ver su información y seguimiento
                  GPS
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeguimientoFlota;
